import React, { useState } from "react";
import { FormField, IconEye, IconEyeOff, ITextInputRaw, TextInputRaw } from "@geotab/zenith";
import LoginTranslations from "../../../helpers/translations";
import type { TTranslate } from "../../../helpers/renderUntilAnswered";

interface IPasswordFieldProps {
    id: string;
    label: string;
    value: string;
    error?: string;
    disabled: boolean;
    textClassName: string;
    translate: TTranslate;
    onChange: (value: string) => void;
}

interface IPasswordInputRawProps extends ITextInputRaw {
    toggle: React.ReactNode;
}

// the field puts its error below this, so the toggle centers on the input alone
const PasswordInputRaw: React.FC<IPasswordInputRawProps> = ({ toggle, ...props }: IPasswordInputRawProps) => (
    <div className="update-password__input">
        <TextInputRaw {...props} />
        {toggle}
    </div>
);

const PasswordInput = FormField(PasswordInputRaw);

const PasswordField: React.FC<IPasswordFieldProps> = ({ id, label, value, error, disabled, textClassName, translate, onChange }: IPasswordFieldProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isEmpty, setIsEmpty] = useState(false);
    const checkIfEmpty = (event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) => setIsEmpty(event.target.value.length === 0);
    const shownError = error || (isEmpty ? translate(LoginTranslations.PasswordRequired) : undefined);

    return (
        <PasswordInput
            id={id}
            label={label}
            labelAlign="top"
            labelClassesName={`update-password__label ${ textClassName }`}
            className="update-password__field"
            type={isVisible ? "text" : "password"}
            autocomplete="off"
            value={value}
            disabled={disabled}
            error={shownError}
            onChange={event => {
                onChange(event.target.value);
                checkIfEmpty(event);
            }}
            onBlur={checkIfEmpty}
            toggle={
                <button
                    id={`${ id }-visible`}
                    className="update-password__visibility"
                    type="button"
                    aria-label={translate(isVisible ? LoginTranslations.HidePassword : LoginTranslations.ShowPassword)}
                    onClick={() => setIsVisible(visible => !visible)}
                >
                    {isVisible ? <IconEyeOff size="huge" /> : <IconEye size="huge" />}
                </button>
            }
        />
    );
};

export default PasswordField;
